/* eslint-disable consistent-return */
import React, {
  useState,
  useCallback,
  forwardRef,
  useEffect,
  SyntheticEvent,
  RefObject,
} from 'react';
import { CSSObject } from 'styled-components';

import {
  Panel as BasePanel,
  IPanelProps as $IPanelProps,
} from '@crm-framework/panel';

import { getIsOpenFromLocalStorage } from './utils';

export interface IPanelProps extends $IPanelProps {
  updateLayout?: () => void;

  isCollapsible?: boolean;
  defaultIsOpen?: boolean;
  panelKey: string;
  key?: string;
}

export type PanelType = IPanelProps & {
  panelStorageName: string;
  style?: CSSObject;
  className?: string;
  onMouseDown?: (e: SyntheticEvent) => void;
  onMouseUp?: (e: SyntheticEvent) => void;
  onTouchEnd?: (e: SyntheticEvent) => void;
};

export const Panel: React.FC<PanelType> = forwardRef<HTMLDivElement, PanelType>(
  (
    {
      defaultIsOpen,
      isCollapsible = true,
      isOpen: isOpenPanel,
      children: Children,
      panelKey,
      className,
      panelStorageName,
      updateLayout,
      onClickArrowButton,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(
      getIsOpenFromLocalStorage({
        isCollapsible,
        panelStorageName,
        panelKey,
        defaultIsOpen,
      })
    );
    const panelClassName = `${className} ${panelKey}`;

    const [bodyHeight, setBodyHeight] = useState(30);

    useEffect(() => {
      const element = (ref as RefObject<HTMLDivElement>).current;

      if (!element) return;

      const observer = new ResizeObserver((entries) => {
        const { height } = entries[0]?.contentRect;

        if (height) {
          setBodyHeight(height);
        }
      });

      observer.observe(element);

      return () => {
        // Cleanup the observer by unobserving all elements
        observer.disconnect();
      };
    }, []);

    useEffect(() => {
      if (isCollapsible) {
        const currentMode = localStorage.getItem(panelStorageName);
        const parsedMode = currentMode ? JSON.parse(currentMode) : {};

        if (panelKey) {
          localStorage.setItem(
            panelStorageName,
            JSON.stringify({ ...parsedMode, [`${panelKey}`]: isOpen })
          );

          if (updateLayout) updateLayout();
        }
      }
    }, [isOpen]);

    const $onClickArrowButton = useCallback(
      (toggle: boolean, e: SyntheticEvent) => {
        if (onClickArrowButton) onClickArrowButton(toggle, e);

        setIsOpen(toggle);
      },
      [isOpen, onClickArrowButton]
    );

    return (
      <BasePanel
        {...props}
        className={panelClassName}
        defaultIsOpen={defaultIsOpen}
        ref={ref}
        isOpen={isOpen}
        onClickArrowButton={$onClickArrowButton}
        bodyHeight={`${bodyHeight ? Number(bodyHeight) - 30 : undefined}px`}>
        {Children}
      </BasePanel>
    );
  }
);
